import { takeLatest, put, call } from "redux-saga/effects";
import {
  POWERBI_REPORTS_DATA,
  POWERBI_REPORTS_GET_API,
  POWERBI_ALL_REPORTS_DATA,
  POWERBI_ALL_REPORTS_GET_API,
  POWERBI_EMBED_URL_DATA,
  POWERBI_EMBED_URL_POST_API,
  POWERBI_REPORT_GET_DATA,
  POWERBI_REPORT_GET_DETAIL_API,
  POWERBI_FAVORITES_DATA,
  POWERBI_FAVORITES_GET_API,
  POWERBI_ADD_FAVORITE,
  POWERBI_ADD_FAVORITE_POST_API,
  POWERBI_REMOVE_FAVORITE,
  POWERBI_REMOVE_FAVORITE_DELETE_API,
} from "./Constants";
import {
  setPowerBIReportsDataSuccess,
  setPowerBIReportsDataFail,
  setPowerBIAllReportsDataSuccess,
  setPowerBIAllReportsDataFail,
  setPowerBIEmbedUrlSuccess,
  setPowerBIEmbedUrlFail,
  setPowerBIReportGetDataSuccess,
  setPowerBIReportGetDataFail,
  setPowerBIFavoritesDataSuccess,
  setPowerBIFavoritesDataFail,
  setPowerBIAddFavoriteSuccess,
  setPowerBIAddFavoriteFail,
  setPowerBIRemoveFavoriteSuccess,
  setPowerBIRemoveFavoriteFail,
} from "./Actions";
import { reportApi } from "../../../shared/components/Service";
import { addErrorAlert } from "../../alert/Actions";

// ==================== Get All Reports ====================
function* getPowerBIReportsDataService() {
  try {
    const reportsResponse = yield call(reportApi.get, POWERBI_REPORTS_GET_API);
    const status = reportsResponse.status;

    if (status === 200) {
      const responseData = reportsResponse.data;
      // Extract employee and reports from the new API structure
      const employee = responseData.data?.employee || {};
      const reports = responseData.data?.reports || [];

      // Store employee data in localStorage for easy access
      if (employee && Object.keys(employee).length > 0) {
        localStorage.setItem('powerbi_employee', JSON.stringify(employee));
      }

      // Pass the reports array to the reducer
      yield put(setPowerBIReportsDataSuccess(reports));
    } else {
      yield put(addErrorAlert("Failed to load reports"));
      yield put(setPowerBIReportsDataFail(reportsResponse.response?.data || "Error loading reports"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading reports"));
    yield put(setPowerBIReportsDataFail(error));
  }
}

// ==================== Get All Reports (No Filter) ====================
function* getPowerBIAllReportsDataService() {
  try {
    const allReportsResponse = yield call(reportApi.get, POWERBI_ALL_REPORTS_GET_API);
    const status = allReportsResponse.status;

    if (status === 200) {
      const responseData = allReportsResponse.data;
      // Extract reports array from response
      const allReports = responseData.data?.reports || responseData.data || [];

      // Pass all reports to the reducer
      yield put(setPowerBIAllReportsDataSuccess(allReports));
    } else {
      yield put(addErrorAlert("Failed to load all reports"));
      yield put(setPowerBIAllReportsDataFail(allReportsResponse.response?.data || "Error loading all reports"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading all reports"));
    yield put(setPowerBIAllReportsDataFail(error));
  }
}

// ==================== Get Embed URL and Token ====================
function* getPowerBIEmbedUrlService({ payload }) {
  try {
    const embedResponse = yield call(reportApi.post, POWERBI_EMBED_URL_POST_API, payload);
    const status = embedResponse.status;

    if (status === 200) {
      // Extract the data from the response - API returns { success: true, data: {...} }
      const responseData = embedResponse.data;
      const data = responseData.data || responseData; // Handle both formats
      yield put(setPowerBIEmbedUrlSuccess(data));
    } else {
      //yield put(addErrorAlert("Something went wrong. Please try again. If the problem persists, contact your administrator."));
      yield put(setPowerBIEmbedUrlFail(embedResponse.response?.data || "Error loading embed URL"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading embed URL"));
    yield put(setPowerBIEmbedUrlFail(error));
  }
}

// ==================== Get Single Report Details ====================
function* getPowerBIReportDataService({ id }) {
  try {
    const reportResponse = yield call(
      reportApi.getDetail,
      POWERBI_REPORT_GET_DETAIL_API + id
    );
    const status = reportResponse.status;

    if (status === 200) {
      const data = reportResponse.data;
      yield put(setPowerBIReportGetDataSuccess(data));
    } else {
      yield put(addErrorAlert("Failed to load report details"));
      yield put(setPowerBIReportGetDataFail(reportResponse.response?.data || "Error loading report"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error loading report details"));
    yield put(setPowerBIReportGetDataFail(error));
  }
}

// ==================== Get Favorite Reports ====================
function* getPowerBIFavoritesDataService() {
  try {
    const favoritesResponse = yield call(reportApi.get, POWERBI_FAVORITES_GET_API);
    const status = favoritesResponse.status;

    if (status === 200) {
      const responseData = favoritesResponse.data;
      // Extract favorites array from response
      const favorites = responseData.data || responseData || [];
      yield put(setPowerBIFavoritesDataSuccess(favorites));
    } else {
      yield put(setPowerBIFavoritesDataFail(favoritesResponse.response?.data || "Error loading favorites"));
    }
  } catch (error) {
    console.error("Error loading favorites:", error);
    yield put(setPowerBIFavoritesDataFail(error));
  }
}

// ==================== Add Favorite Report ====================
function* addPowerBIFavoriteService({ reportId }) {
  try {
    // reportId here is the primary key (id) from the reports table
    const payload = { report_id: reportId };
    const addFavoriteResponse = yield call(reportApi.post, POWERBI_ADD_FAVORITE_POST_API, payload);
    const status = addFavoriteResponse.status;

    if (status === 200 || status === 201) {
      yield put(setPowerBIAddFavoriteSuccess(reportId));
    } else {
      yield put(addErrorAlert("Failed to add favorite"));
      yield put(setPowerBIAddFavoriteFail(addFavoriteResponse.response?.data || "Error adding favorite"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error adding favorite"));
    yield put(setPowerBIAddFavoriteFail(error));
  }
}

// ==================== Remove Favorite Report ====================
function* removePowerBIFavoriteService({ reportId }) {
  try {
    const deleteFavoriteResponse = yield call(
      reportApi.delete,
      POWERBI_REMOVE_FAVORITE_DELETE_API + reportId
    );
    const status = deleteFavoriteResponse.status;

    if (status === 200 || status === 204) {
      yield put(setPowerBIRemoveFavoriteSuccess(reportId));
    } else {
      yield put(addErrorAlert("Failed to remove favorite"));
      yield put(setPowerBIRemoveFavoriteFail(deleteFavoriteResponse.response?.data || "Error removing favorite"));
    }
  } catch (error) {
    yield put(addErrorAlert("Error removing favorite"));
    yield put(setPowerBIRemoveFavoriteFail(error));
  }
}

// ==================== Watcher Saga ====================
export function* watchPowerBIReports() {
  yield takeLatest(
    POWERBI_REPORTS_DATA.POWERBI_REPORTS_DATA_LOAD,
    getPowerBIReportsDataService
  );
  yield takeLatest(
    POWERBI_ALL_REPORTS_DATA.POWERBI_ALL_REPORTS_DATA_LOAD,
    getPowerBIAllReportsDataService
  );
  yield takeLatest(
    POWERBI_EMBED_URL_DATA.POWERBI_EMBED_URL_DATA_LOAD,
    getPowerBIEmbedUrlService
  );
  yield takeLatest(
    POWERBI_REPORT_GET_DATA.POWERBI_REPORT_GET_DATA_LOAD,
    getPowerBIReportDataService
  );
  yield takeLatest(
    POWERBI_FAVORITES_DATA.POWERBI_FAVORITES_DATA_LOAD,
    getPowerBIFavoritesDataService
  );
  yield takeLatest(
    POWERBI_ADD_FAVORITE.POWERBI_ADD_FAVORITE_LOAD,
    addPowerBIFavoriteService
  );
  yield takeLatest(
    POWERBI_REMOVE_FAVORITE.POWERBI_REMOVE_FAVORITE_LOAD,
    removePowerBIFavoriteService
  );
}
